import type { SelfServiceRequest } from "../types";
import { HISTORY_REQUEST_STATUSES } from "./selfRequestRules";

export type StatusGroup = "draft" | "review" | "approved";

export type AmendmentType = "position" | "salary" | "workSchedule" | "clause";

export function getStatusGroup(status: string): StatusGroup {
  if (status === "draft") return "draft";
  if (
    status === "signing_pending" ||
    status === "pending_review" ||
    status === "pending_signing"
  )
    return "review";
  return "approved";
}

export function isActiveRequest(request: SelfServiceRequest): boolean {
  return !HISTORY_REQUEST_STATUSES.includes(request.status as (typeof HISTORY_REQUEST_STATUSES)[number]);
}

export function getAmendmentType(request: SelfServiceRequest): AmendmentType {
  const clauseSection = request.details?.clause_section as string | null;
  if (clauseSection === "1.1") return "position";
  if (clauseSection === "4.12") return "salary";
  if (clauseSection === "4.1") return "workSchedule";

  const newValues = request.details?.new_values as Record<string, unknown> | null;
  if (newValues) {
    if ("salary_amount" in newValues) return "salary";
    if ("job_position_ru" in newValues || "job_position_kk" in newValues) return "position";
  }
  return "clause";
}

export function getBlockedAmendmentTypes(
  requests: SelfServiceRequest[] | undefined,
): Map<AmendmentType, { request: SelfServiceRequest; statusGroup: StatusGroup }> {
  const blocked = new Map<AmendmentType, { request: SelfServiceRequest; statusGroup: StatusGroup }>();
  if (!requests) return blocked;

  for (const request of requests) {
    if (!isActiveRequest(request)) continue;
    const amendmentType = getAmendmentType(request);
    if (!blocked.has(amendmentType)) {
      blocked.set(amendmentType, { request, statusGroup: getStatusGroup(request.status) });
    }
  }

  return blocked;
}

const ACTIVE_DATA_CHANGE_STATUSES = ["pending_hr", "hr_approved"];

export function getActiveDataChangeRequest(
  requests: SelfServiceRequest[] | undefined,
): { request: SelfServiceRequest; statusGroup: StatusGroup } | null {
  if (!requests) return null;

  for (const request of requests) {
    if (request.type === "data_change_request" && ACTIVE_DATA_CHANGE_STATUSES.includes(request.status)) {
      return { request, statusGroup: getStatusGroup(request.status) };
    }
  }

  return null;
}

export function getActiveResignation(
  requests: SelfServiceRequest[] | undefined,
): { request: SelfServiceRequest; statusGroup: StatusGroup } | null {
  if (!requests) return null;

  for (const request of requests) {
    if (isActiveRequest(request)) {
      return { request, statusGroup: getStatusGroup(request.status) };
    }
  }

  return null;
}
